import { VARIABLES } from '@app/components/common/constants';
import { FORECAST_MODELS, ForecastModel } from '@app/common/static-data/models';
import { InternalVariable } from '@app/types/common';

import { PlottableForecast } from '../types';

type ModelId = (typeof FORECAST_MODELS)[number]['id'];

type ChartDataPoint = {
  time: string;
  [key: `${string}_model_${string}`]: number;
  [key: `${string}_model_${string}_q5`]: number;
  [key: `${string}_model_${string}_q95`]: number;
  [key: `${string}_model_${string}_q25`]: number;
  [key: `${string}_model_${string}_q75`]: number;
  // Add metadata for confidence intervals
  _confidenceIntervals?: Record<string, { q5?: number; q95?: number; q25?: number; q75?: number }>;
};

/**
 * Helper function to assign quantile values to a chart data point
 */
function assignQuantiles(
  point: ChartDataPoint,
  selectedParam: string,
  modelId: string,
  values: { q5?: number; q95?: number; q25?: number; q75?: number },
) {
  if (values.q5 !== undefined && values.q95 !== undefined) {
    point[`${selectedParam}_model_${modelId}_q5`] = values.q5;
    point[`${selectedParam}_model_${modelId}_q95`] = values.q95;
  }
  if (values.q25 !== undefined && values.q75 !== undefined) {
    point[`${selectedParam}_model_${modelId}_q25`] = values.q25;
    point[`${selectedParam}_model_${modelId}_q75`] = values.q75;
  }
}

/**
 * Helper function to interpolate quantile values
 */
function interpolateQuantiles(
  prevValues: { q5?: number; q95?: number; q25?: number; q75?: number },
  nextValues: { q5?: number; q95?: number; q25?: number; q75?: number },
  ratio: number,
): { q5?: number; q95?: number; q25?: number; q75?: number } {
  const result: { q5?: number; q95?: number; q25?: number; q75?: number } = {};

  // Interpolate q5/q95 if available
  if (
    prevValues.q5 !== undefined &&
    prevValues.q95 !== undefined &&
    nextValues.q5 !== undefined &&
    nextValues.q95 !== undefined
  ) {
    result.q5 = prevValues.q5 + (nextValues.q5 - prevValues.q5) * ratio;
    result.q95 = prevValues.q95 + (nextValues.q95 - prevValues.q95) * ratio;
  }

  // Interpolate q25/q75 if available
  if (
    prevValues.q25 !== undefined &&
    prevValues.q75 !== undefined &&
    nextValues.q25 !== undefined &&
    nextValues.q75 !== undefined
  ) {
    result.q25 = prevValues.q25 + (nextValues.q25 - prevValues.q25) * ratio;
    result.q75 = prevValues.q75 + (nextValues.q75 - prevValues.q75) * ratio;
  }

  return result;
}

/**
 * Helper function to store confidence interval metadata
 */
function storeConfidenceIntervalMetadata(
  point: ChartDataPoint,
  modelId: string,
  values: { q5?: number; q95?: number; q25?: number; q75?: number },
) {
  if (
    (values.q5 !== undefined && values.q95 !== undefined) ||
    (values.q25 !== undefined && values.q75 !== undefined)
  ) {
    if (!point._confidenceIntervals) {
      point._confidenceIntervals = {};
    }
    point._confidenceIntervals[modelId] = {
      ...(values.q5 !== undefined && values.q95 !== undefined
        ? { q5: values.q5, q95: values.q95 }
        : {}),
      ...(values.q25 !== undefined && values.q75 !== undefined
        ? { q25: values.q25, q75: values.q75 }
        : {}),
    };
  }
}

export function formatChartData(
  data: Record<ModelId, PlottableForecast>,
  selectedModels: ForecastModel[],
  selectedParam: string,
) {
  if (!data || !selectedModels.length) return [];

  const paramMapping = VARIABLES.find((v) => v.juaId === selectedParam);
  if (!paramMapping) throw Error("Can't find the relevant parameter");

  // Find the overlapping time range
  let minTime = new Date(8640000000000000); // Max date
  let maxTime = new Date(-8640000000000000); // Min date

  // First pass: determine the overlapping time range
  selectedModels.forEach((modelId) => {
    const forecast = data[modelId];
    if (!forecast?.time?.length || !forecast.data[selectedParam as InternalVariable]) return;

    const forecastMinTime = new Date(Math.min(...forecast.time.map((t) => t.getTime())));
    const forecastMaxTime = new Date(Math.max(...forecast.time.map((t) => t.getTime())));

    minTime = new Date(Math.min(minTime.getTime(), forecastMinTime.getTime()));
    maxTime = new Date(Math.max(maxTime.getTime(), forecastMaxTime.getTime()));
  });

  // Create unified time axis with hourly steps
  const timeAxis: Date[] = [];
  const currentTime = new Date(minTime);
  while (currentTime <= maxTime) {
    timeAxis.push(new Date(currentTime));
    currentTime.setUTCHours(currentTime.getUTCHours() + 1);
  }

  // Initialize chart data array with the unified time axis
  const chartData: ChartDataPoint[] = timeAxis.map(
    (time) =>
      ({
        time: time.toISOString(),
        _confidenceIntervals: {},
      }) as ChartDataPoint,
  );

  // Map each forecast's data to the unified time axis, with linear interpolation for missing values
  selectedModels.forEach((modelId) => {
    const forecast = data[modelId];
    if (!forecast?.data[selectedParam as InternalVariable]) return;

    // Build array of { time, value } for known points
    const knownPoints = forecast.time
      .map((time, idx) => ({
        time: time.toISOString(),
        value: forecast.data[selectedParam as InternalVariable]?.[idx],
        q5: forecast.stats?.[selectedParam as InternalVariable]?.q5?.[idx],
        q95: forecast.stats?.[selectedParam as InternalVariable]?.q95?.[idx],
        q25: forecast.stats?.[selectedParam as InternalVariable]?.q25?.[idx],
        q75: forecast.stats?.[selectedParam as InternalVariable]?.q75?.[idx],
      }))
      .filter((p) => p.value !== undefined && p.value !== null && !isNaN(p.value));

    // For each point in the chartData (hourly axis)
    chartData.forEach((point) => {
      // If value exists, just use it
      const known = knownPoints.find((p) => p.time === point.time);
      if (known) {
        point[`${selectedParam}_model_${modelId}`] = known.value!;
        assignQuantiles(point, selectedParam, modelId, {
          q5: known.q5,
          q95: known.q95,
          q25: known.q25,
          q75: known.q75,
        });
        storeConfidenceIntervalMetadata(point, modelId, {
          q5: known.q5,
          q95: known.q95,
          q25: known.q25,
          q75: known.q75,
        });
        return;
      }
      // Otherwise, interpolate
      // Find previous and next known points
      const pointTime = new Date(point.time).getTime();
      let prevIdx = -1,
        nextIdx = -1;
      for (let k = 0; k < knownPoints.length; k++) {
        const t = new Date(knownPoints[k].time).getTime();
        if (t < pointTime) prevIdx = k;
        if (t > pointTime) {
          nextIdx = k;
          break;
        }
      }
      if (prevIdx !== -1 && nextIdx !== -1) {
        const prev = knownPoints[prevIdx];
        const next = knownPoints[nextIdx];
        const t0 = new Date(prev.time).getTime();
        const t1 = new Date(next.time).getTime();
        const v0 = prev.value!;
        const v1 = next.value!;
        // Linear interpolation for main value
        const ratio = (pointTime - t0) / (t1 - t0);
        point[`${selectedParam}_model_${modelId}`] = v0 + (v1 - v0) * ratio;

        // Interpolate quantiles using helper function
        const interpolatedQuantiles = interpolateQuantiles(
          { q5: prev.q5, q95: prev.q95, q25: prev.q25, q75: prev.q75 },
          { q5: next.q5, q95: next.q95, q25: next.q25, q75: next.q75 },
          ratio,
        );

        // Assign interpolated quantiles to the point
        assignQuantiles(point, selectedParam, modelId, interpolatedQuantiles);

        // Store confidence interval metadata for easy tooltip access
        storeConfidenceIntervalMetadata(point, modelId, interpolatedQuantiles);
      }
    });
  });
  return chartData.sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());
}
